
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Loader2, AlertTriangle } from 'lucide-react';
import { StripeTransaction } from '@/types/stripe-finances';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

interface TransactionsTableProps {
  isLoading: boolean;
  error: Error | null;
  transactions: StripeTransaction[] | null;
}

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TransactionsTable: React.FC<TransactionsTableProps> = ({ isLoading, error, transactions }) => {
  const { t } = useTranslation('finances');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <FileText className="mr-2 h-5 w-5" />
          {t('transactions.title')}
        </CardTitle>
        <CardDescription>
          {t('transactions.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {error && (
          <div className="flex items-center text-red-500">
            <AlertTriangle className="mr-2 h-5 w-5" />
            <span>{t('transactions.failedToLoad')}</span>
          </div>
        )}

        {!isLoading && !error && transactions && (
          <>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('transactions.noTransactions')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('transactions.date')}</TableHead>
                      <TableHead>{t('transactions.description')}</TableHead>
                      <TableHead>{t('transactions.amount')}</TableHead>
                      <TableHead>{t('transactions.netAmount')}</TableHead>
                      <TableHead>{t('transactions.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.created)}</TableCell>
                        <TableCell>{transaction.description || t('transactions.payment')}</TableCell>
                        <TableCell>{formatCurrency(transaction.amount, transaction.currency)}</TableCell>
                        <TableCell>{formatCurrency(transaction.net, transaction.currency)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            transaction.status === 'succeeded' 
                              ? 'bg-green-100 text-green-800' 
                              : transaction.status === 'pending' 
                                ? 'bg-amber-100 text-amber-800' 
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {transaction.status === 'succeeded' ? t('transactions.succeeded') : 
                             transaction.status === 'pending' ? t('transactions.pending') :
                             transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionsTable;
